// RegistryViewGroups.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Utilities
import {
  createViewGroup,
  moveViewGroupById,
  normalizeViewGroups,
  reindexViewGroups,
} from 'utilities/ViewGroups';

// CSS
import './RegistryViewGroups.css';

// Components
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';
import RegistryViewGroupRow from 'admin/nodetypes/viewgroups/RegistryViewGroupRow';
import TextInput from 'components/textinput/TextInput';
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';

/**
 * RegistryViewGroups
 *
 * Sortable editor for type-scoped view groups.
 *
 * @param {array} viewGroups - Current view groups.
 * @param {function} onChange - Updates view groups.
 *
 * @returns {ReactNode}
 */
const RegistryViewGroups = ({
  viewGroups = [],
  onChange,
}) => {
  // ----- STATE ----- //

  const [orderedGroups, setOrderedGroups] = useState(() => normalizeViewGroups(viewGroups));
  const [draggingId, setDraggingId] = useState('');
  const [dragOverId, setDragOverId] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftDescription, setDraftDescription] = useState('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setOrderedGroups(normalizeViewGroups(viewGroups));
  }, [viewGroups]);

  // ----- HANDLERS ----- //

  const commitGroups = useCallback((next) => {
    const normalized = reindexViewGroups(next);
    setOrderedGroups(normalized);
    onChange?.(normalized);
  }, [onChange]);

  const openCreate = useCallback(() => {
    setEditingId('');
    setDraftLabel('');
    setDraftDescription('');
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((group) => {
    setEditingId(String(group?.id || '').trim());
    setDraftLabel(String(group?.label || '').trim());
    setDraftDescription(String(group?.description || '').trim());
    setEditorOpen(true);
  }, []);

  const handleSaveEditor = useCallback(() => {
    const label = String(draftLabel || '').trim();
    if (!label) return;

    if (editingId) {
      commitGroups(orderedGroups.map((group) => (
        group.id === editingId
          ? { ...group, label, description: String(draftDescription || '').trim() }
          : group
      )));
    } else {
      const created = createViewGroup({
        label,
        description: draftDescription,
      });
      commitGroups([...orderedGroups, created]);
    }

    setEditorOpen(false);
  }, [commitGroups, draftDescription, draftLabel, editingId, orderedGroups]);

  const handleDelete = useCallback((groupId) => {
    const id = String(groupId || '').trim();
    if (!id) return;
    commitGroups(orderedGroups.filter((group) => group.id !== id));
  }, [commitGroups, orderedGroups]);

  const handleDragStart = useCallback((groupId) => {
    setDraggingId(String(groupId || '').trim());
  }, []);

  const handleDragOver = useCallback((groupId) => {
    const id = String(groupId || '').trim();
    if (!id || id === draggingId) return;
    setDragOverId(id);
  }, [draggingId]);

  const handleDragEnd = useCallback(() => {
    setDraggingId('');
    setDragOverId('');
  }, []);

  const handleDrop = useCallback((targetId) => {
    const next = moveViewGroupById(orderedGroups, draggingId, targetId);
    handleDragEnd();
    if (!next) return;
    commitGroups(next);
  }, [commitGroups, draggingId, handleDragEnd, orderedGroups]);

  // ----- RENDER ----- //

  return (
    <div className="registry-view-groups">
      <DatabaseSettingsAddButton
        title="Add View Group"
        onClick={openCreate}
      />

      <div className="registry-view-groups-list">
        {orderedGroups.map((group) => (
          <RegistryViewGroupRow
            key={group.id}
            group={group}
            isDragging={draggingId === group.id}
            isDropTarget={dragOverId === group.id && draggingId !== group.id}
            onClick={() => openEdit(group)}
            onDelete={() => handleDelete(group.id)}
            onDragEnd={handleDragEnd}
            onDragOver={() => handleDragOver(group.id)}
            onDragStart={() => handleDragStart(group.id)}
            onDrop={() => handleDrop(group.id)}
            onSettingsClick={() => openEdit(group)}
          />
        ))}
      </div>

      {!orderedGroups.length ? (
        <div className="registry-view-groups-empty">
          No view groups yet.
        </div>
      ) : null}

      <Modal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editingId ? 'Edit View Group' : 'Add View Group'}
      >
        <ModalForm>
          <ModalField>
            <ModalLabel label="Label" />
            <ModalHint hint="Shown as a dropdown in the collection view switcher." />
            <TextInput
              value={draftLabel}
              placeholder="e.g. Kitchen & Bath"
              maxLength={80}
              autoFocus
              autoSelect
              onChange={(event) => setDraftLabel(event.target.value)}
            />
          </ModalField>
          <ModalField>
            <ModalLabel label="Description" />
            <ModalHint hint="Optional context for builders." />
            <TextInput
              value={draftDescription}
              placeholder="Optional short description…"
              maxLength={240}
              multiline
              expandable
              onChange={(event) => setDraftDescription(event.target.value)}
            />
          </ModalField>
        </ModalForm>
        <ModalButtons>
          <Button
            label="Cancel"
            onClick={() => setEditorOpen(false)}
          />
          <Button
            iconKey="check"
            label="Save"
            enabled={Boolean(String(draftLabel || '').trim())}
            onClick={handleSaveEditor}
          />
        </ModalButtons>
      </Modal>
    </div>
  );
};

RegistryViewGroups.displayName = 'RegistryViewGroups';

export default RegistryViewGroups;
